import { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  IconButton,
  Card,
  CardContent,
  Button,
  Divider,
  useTheme,
  Checkbox,
  Chip,
  Tooltip,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CloseIcon from '@mui/icons-material/Close'
import { useNavigate, useLocation } from 'react-router-dom'
import aiIcon from 'src/assets/siri.png'
import { planDetail } from 'src/assets/data'
import { customAddOns } from './UsageData'
import { styled } from '@mui/material/styles'
import FormControlLabel from '@mui/material/FormControlLabel'
import Switch from '@mui/material/Switch'
import AddCreditsSlider from './AddCreditsSlider'
import { planNames } from 'src/config'
import { toast } from 'react-hot-toast'
import { useUpdatePlanMutation, useGetCurrentPlanQuery } from 'src/services/billing-service.js'

const ADD_ON_PRICES = {
  dfyAccounts: (mailboxes = 3, domains = 1) => 3.5 * mailboxes + 12 * domains,
  mailbox: 1,
  emailVerification: 10,
  vvSupport: 0,
  visitorIdentification: 0,
  sms: 0,
  linkedIn: 20,
  whatsapp: 20,
}

const IOSSwitch = styled(props => <Switch focusVisibleClassName='.Mui-focusVisible' disableRipple {...props} />)(
  ({ theme }) => ({
    width: 42,
    height: 26,
    padding: 0,
    '& .MuiSwitch-switchBase': {
      padding: 0,
      margin: 2,
      transitionDuration: '300ms',
      '&.Mui-checked': {
        transform: 'translateX(16px)',
        color: '#fff',
        '& + .MuiSwitch-track': {
          backgroundColor: '#65C466',
          opacity: 1,
          border: 0,
          ...theme.applyStyles('dark', {
            backgroundColor: '#2ECA45',
          }),
        },
        '&.Mui-disabled + .MuiSwitch-track': {
          opacity: 0.5,
        },
      },
      '&.Mui-focusVisible .MuiSwitch-thumb': {
        color: '#33cf4d',
        border: '6px solid #fff',
      },
      '&.Mui-disabled .MuiSwitch-thumb': {
        color: theme.palette.grey[100],
        ...theme.applyStyles('dark', {
          color: theme.palette.grey[600],
        }),
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 0.7,
        ...theme.applyStyles('dark', {
          opacity: 0.3,
        }),
      },
    },
    '& .MuiSwitch-thumb': {
      boxSizing: 'border-box',
      width: 22,
      height: 22,
    },
    '& .MuiSwitch-track': {
      borderRadius: 26 / 2,
      backgroundColor: '#E9E9EA',
      opacity: 1,
      transition: theme.transitions.create(['background-color'], {
        duration: 500,
      }),
      ...theme.applyStyles('dark', {
        backgroundColor: '#39393D',
      }),
    },
  })
)

const CustomizePlan = () => {
  const location = useLocation()
  const state = location.state
  const [updatePlan] = useUpdatePlanMutation()
  const { data: fetchedCurrentPlan, refetch: refetchCurrentPlan } = useGetCurrentPlanQuery()
  const [selectedLeadsPlan, setSelectedLeadsPlan] = useState(state.type === 'LEADS' ? state.price_id : null)
  const [selectedOutreachPlan, setSelectedOutreachPlan] = useState(state.type === 'OUTREACH' ? state.price_id : null)
  const [selectedAddOns, setSelectedAddOns] = useState([])
  const [selectedAddOnsPrice, setSelectedAddOnsPrice] = useState(0)
  const [isSwitchOn, setIsSwitchOn] = useState(!state.package)
  const [emailCreditsDetails, setEmailCreditsDetails] = useState({
    credits: 2500,
    period: 'monthly',
    price: 10,
  })
  const [total, setTotal] = useState(0)
  const theme = useTheme()
  const navigate = useNavigate()

  const handleSelect = (id, type) => {
    if (type === 'leads') {
      setSelectedLeadsPlan(selectedLeadsPlan === id ? null : id)
    } else if (type === 'outreach') {
      setSelectedOutreachPlan(selectedOutreachPlan === id ? null : id)
    }
  }

  const planCard = [
    {
      leadsPlan: isSwitchOn ? planDetail.LEADS.YEARLY : planDetail.LEADS.MONTHLY,
      outreachPlan: isSwitchOn ? planDetail.OUTREACH.YEARLY : planDetail.OUTREACH.MONTHLY,
    },
  ]

  const handleConfirmPlanUpdate = async () => {
    const selectedPlans = {
      leads: selectedLeadsPlan,
      outreach: selectedOutreachPlan,
    }

    const basePlans = Object.values(selectedPlans)
      .filter(Boolean)
      .map(priceId => ({
        priceId,
        quantity: 1,
      }))

    const addOns = selectedAddOns.map(addon => {
      if (addon === 'emailVerification') {
        return {
          priceId: addon,
          quantity: emailCreditsDetails.credits / 2500,
          type: 'addon',
        }
      } else if (addon === 'dfyAccounts') {
        return {
          priceId: addon,
          quantity: 1,
          mailboxes: 3,
          domains: 1,
          type: 'addon',
        }
      } else {
        return {
          priceId: addon,
          quantity: 1,
          type: 'addon',
        }
      }
    })

    if (basePlans.length === 0) {
      toast.error('Please select at least one plan', { duration: 5000 })
      return
    }

    const toastId = toast.loading('Updating...', { duration: Number.POSITIVE_INFINITY })
    try {
      const subscription = {
        basePlans,
        workspaceId: state?.workspace || state?.workspaceID,
        planName: basePlans.map(plan => planNames[plan.priceId]?.toLowerCase().split(' ')[1] || '').join(', '),
      }

      const session = await updatePlan(subscription).unwrap()

      if (session.url) {
        window.location.href = session.url
      } else if (session.errorMsg) {
        toast.error(session.errorMsg, { id: toastId, duration: 5000 })
      } else {
        toast.success('Plan Updated', { id: toastId, duration: 2000 })
        await refetchCurrentPlan()
        navigate('/onboardinghub')
      }
    } catch (error) {
      toast.error('An unexpected error occurred during update', { id: toastId, duration: 5000 })
    }
  }

  const handleToggleAddOn = id => {
    setSelectedAddOns(prevSelected =>
      prevSelected.includes(id) ? prevSelected.filter(item => item !== id) : [...prevSelected, id]
    )
  }

  useEffect(() => {
    const calculateTotal = () => {
      return selectedAddOns.reduce((acc, id) => {
        if (id === 'emailVerification') {
          acc += emailCreditsDetails?.price || 0
        } else {
          const price = ADD_ON_PRICES[id]
          if (typeof price === 'function') {
            acc += price()
          }
          if (typeof price === 'number') {
            acc += isSwitchOn ? price * 12 : price
          }
        }
        return acc
      }, 0)
    }
    setSelectedAddOnsPrice(calculateTotal())
  }, [selectedAddOns, emailCreditsDetails, isSwitchOn])

  useEffect(() => {
    const period = isSwitchOn ? 'YEARLY' : 'MONTHLY'

    const leadsPrice = Number.parseFloat(
      planDetail.LEADS[period][
        Object.keys(planDetail.LEADS[period]).find(key => planDetail.LEADS[period][key].priceId === selectedLeadsPlan)
      ]?.price?.replace(/[^0-9.]/g, '') || 0
    )

    const outreachPrice = Number.parseFloat(
      planDetail.OUTREACH[period][
        Object.keys(planDetail.OUTREACH[period]).find(
          key => planDetail.OUTREACH[period][key].priceId === selectedOutreachPlan
        )
      ]?.price?.replace(/[^0-9.]/g, '') || 0
    )

    const basePlanPrice = leadsPrice + outreachPrice
    const yearlyBasePrice = (leadsPrice + outreachPrice) * 12

    setTotal(selectedAddOnsPrice + (isSwitchOn ? yearlyBasePrice : basePlanPrice))
  }, [selectedLeadsPlan, selectedOutreachPlan, selectedAddOnsPrice, isSwitchOn])

  const isCurrentPlan = priceId => {
    return (
      fetchedCurrentPlan?.subscription?.leads?.planId === priceId ||
      fetchedCurrentPlan?.subscription?.sendingWarmup?.planId === priceId
    )
  }

  const convertPriceId = currentPriceId => {
    let currentMode = 'LEADS'
    const currentPeriod = isSwitchOn ? 'YEARLY' : 'MONTHLY'
    const targetPeriod = isSwitchOn ? 'MONTHLY' : 'YEARLY'

    if (
      Object.values(planDetail.OUTREACH.MONTHLY).some(p => p.priceId === currentPriceId) ||
      Object.values(planDetail.OUTREACH.YEARLY).some(p => p.priceId === currentPriceId)
    ) {
      currentMode = 'OUTREACH'
    }

    const planLevel = Object.keys(planDetail[currentMode][currentPeriod]).find(
      key => planDetail[currentMode][currentPeriod][key].priceId === currentPriceId
    )

    return planLevel ? planDetail[currentMode][targetPeriod][planLevel].priceId : null
  }

  const isYearlyPlan = priceId => {
    return (
      Object.values(planDetail.LEADS.YEARLY).some(p => p.priceId === priceId) ||
      Object.values(planDetail.OUTREACH.YEARLY).some(p => p.priceId === priceId)
    )
  }

  const handleSwitchChange = e => {
    const newIsYearly = e.target.checked

    const hasYearlyPlan =
      isYearlyPlan(fetchedCurrentPlan?.subscription?.leads?.planId) ||
      isYearlyPlan(fetchedCurrentPlan?.subscription?.sendingWarmup?.planId)

    if (hasYearlyPlan && !newIsYearly) {
      toast.error('You cannot downgrade from a yearly plan to monthly')
      return
    }

    setIsSwitchOn(newIsYearly)

    if (selectedLeadsPlan) {
      const newLeadsPriceId = convertPriceId(selectedLeadsPlan)
      setSelectedLeadsPlan(newLeadsPriceId)
    }

    if (selectedOutreachPlan) {
      const newOutreachPriceId = convertPriceId(selectedOutreachPlan)
      setSelectedOutreachPlan(newOutreachPriceId)
    }
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          flexDirection: 'column',
          bgcolor: 'background.default',
          px: 2,
          pt: 2,
          maxWidth: '900px',
          m: '0 auto',
          maxHeight: '95%',
          justifyContent: 'space-between',
          pb: { xs: '200px', sm: '200px', md: '70px' },
        }}
      >
        <Box sx={{ position: 'relative' }}>
          <IconButton
            sx={{ position: 'absolute', left: 0, top: 0 }}
            color='primary'
            onClick={() => navigate('/settingsUpdated')}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant='h6' align='center' sx={{ fontWeight: 'bold' }}>
            Build your own plan
          </Typography>
          <IconButton sx={{ position: 'absolute', right: 0, top: 0 }} onClick={() => navigate('/settingsUpdated')}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box>
          <Typography variant='h6' sx={{ mt: 4, mb: 2 }}>
            1. Select your products
          </Typography>

          <Box sx={{ display: 'flex', mt: 3, mb: 1, alignItems: 'center' }}>
            <img src={aiIcon || '/placeholder.svg'} alt='aiIcon' width={30} />
            <Typography variant='h6' sx={{ mr: 2 }}>
              DATABASE
            </Typography>
            {selectedLeadsPlan && (
              <Button
                size='small'
                variant='outlined'
                color='secondary'
                sx={{ ml: 1, fontSize: '12px', py: 0.2, px: 1 }}
                onClick={() => setSelectedLeadsPlan(null)}
              >
                Deselect
              </Button>
            )}
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1.5,
              justifyContent: 'space-between',
            }}
          >
            {Object.entries(planCard[0].leadsPlan).map(([planName, planData], i) => (
              <Card
                key={i}
                variant='outlined'
                sx={{
                  borderColor: planData.priceId === selectedLeadsPlan ? theme.palette.primary.main : 'divider',
                  borderWidth: planData.priceId === selectedLeadsPlan ? 2 : 1,
                  cursor: isCurrentPlan(planData.priceId) ? 'not-allowed' : 'pointer',
                  position: 'relative',
                  opacity: isCurrentPlan(planData.priceId) ? 0.7 : 1,
                  width: { xs: '100%', sm: 'calc(33.33% - 8px)' },
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                }}
                onClick={() => !isCurrentPlan(planData.priceId) && handleSelect(planData.priceId, 'leads')}
              >
                <CardContent sx={{ p: 1.5, flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <Typography
                    variant='h6'
                    sx={{ fontSize: { xs: '12px', sm: '13px', md: '14px' }, mb: 0.5 }}
                  >
                    {planName.charAt(0) + planName.slice(1).toLowerCase()}
                  </Typography>
                  <Typography
                    variant='body2'
                    color='text.secondary'
                    sx={{ fontSize: { xs: '11px', sm: '12px', md: '13px' } }}
                  >
                    {planData.price}/{isSwitchOn ? 'month' : 'month'}
                  </Typography>
                </CardContent>
                {isCurrentPlan(planData.priceId) && (
                  <Box sx={{ p: 1.5, pt: 0, height: '20px' }}>
                    <Typography
                      variant='caption'
                      sx={{
                        color: 'text.secondary',
                        display: 'block',
                        fontStyle: 'italic',
                        fontSize: '10px',
                      }}
                    >
                      Current Plan
                    </Typography>
                  </Box>
                )}
              </Card>
            ))}
          </Box>

          <Box sx={{ display: 'flex', mt: 3, mb: 1, alignItems: 'center' }}>
            <img src={aiIcon || '/placeholder.svg'} alt='aiIcon' width={30} />
            <Typography variant='h6' sx={{ mr: 2 }}>
              Outreach
            </Typography>
            {selectedOutreachPlan && (
              <Button
                size='small'
                variant='outlined'
                color='secondary'
                sx={{ ml: 1, fontSize: '12px', py: 0.2, px: 1 }}
                onClick={() => setSelectedOutreachPlan(null)}
              >
                Deselect
              </Button>
            )}
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1.5,
              justifyContent: 'space-between',
            }}
          >
            {Object.entries(planCard[0].outreachPlan).map(([planName, planData], i) => (
              <Card
                key={i}
                variant='outlined'
                sx={{
                  borderColor: planData.priceId === selectedOutreachPlan ? theme.palette.primary.main : 'divider',
                  borderWidth: planData.priceId === selectedOutreachPlan ? 2 : 1,
                  cursor: isCurrentPlan(planData.priceId) ? 'not-allowed' : 'pointer',
                  position: 'relative',
                  opacity: isCurrentPlan(planData.priceId) ? 0.7 : 1,
                  width: { xs: '100%', sm: 'calc(33.33% - 8px)' },
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                }}
                onClick={() => !isCurrentPlan(planData.priceId) && handleSelect(planData.priceId, 'outreach')}
              >
                <CardContent sx={{ p: 1.5, flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <Typography
                    variant='h6'
                    sx={{ fontSize: { xs: '12px', sm: '13px', md: '14px' }, mb: 0.5 }}
                  >
                    {planName.charAt(0) + planName.slice(1).toLowerCase()}
                  </Typography>
                  <Typography
                    variant='body2'
                    color='text.secondary'
                    sx={{ fontSize: { xs: '11px', sm: '12px', md: '13px' } }}
                  >
                    {planData.price}/{isSwitchOn ? 'month' : 'month'}
                  </Typography>
                </CardContent>
                {isCurrentPlan(planData.priceId) && (
                  <Box sx={{ p: 1.5, pt: 0, height: '20px' }}>
                    <Typography
                      variant='caption'
                      sx={{
                        color: 'text.secondary',
                        display: 'block',
                        fontStyle: 'italic',
                        fontSize: '10px',
                      }}
                    >
                      Current Plan
                    </Typography>
                  </Box>
                )}
              </Card>
            ))}
          </Box>
        </Box>

        <Box>
          <Typography variant='h6' sx={{ mt: 4, mb: 2 }}>
            2. Select an add-on
          </Typography>
          <Box sx={{ mt: 2 }}>
            {customAddOns.map(adOn => (
              <Box key={adOn.id} sx={{ mb: 2 }}>
                <Card
                  variant='outlined'
                  sx={{
                    borderColor: selectedAddOns.includes(adOn.id) ? theme.palette.primary.main : 'divider',
                    borderWidth: selectedAddOns.includes(adOn.id) ? 2 : 1,
                    cursor: 'pointer',
                    position: 'relative',
                    height: 'fit-content',
                    minHeight: 'auto',
                  }}
                >
                  <CardContent sx={{ pb: 2 }}>
                    <Checkbox
                      checked={Array.isArray(selectedAddOns) && selectedAddOns.includes(adOn.id)}
                      value={adOn.id}
                      disabled={adOn.comingSoon}
                      onChange={() => handleToggleAddOn(adOn.id)}
                      sx={{ position: 'absolute', top: 8, right: 8 }}
                    />
                    <Typography variant='h6'>
                      {adOn.title}{' '}
                      <Chip
                        label={`${adOn.price}/${isSwitchOn ? 'month' : 'month'}`}
                        sx={{ backgroundColor: '#216fed', color: '#fff', fontWeight: 'bold' }}
                      />
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      {adOn.description}
                    </Typography>
                  </CardContent>
                </Card>
                {adOn.id === 'emailVerification' && selectedAddOns.includes('emailVerification') && (
                  <Box mt={2}>
                    <AddCreditsSlider
                      onPriceChange={details => setEmailCreditsDetails(details)}
                      period={isSwitchOn ? 'yearly' : 'monthly'}
                    />
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: '53%',
          transform: 'translateX(-50%)',
          width: '100%',
          maxWidth: '950px',
          bgcolor: 'background.paper',
          boxShadow: '0 -2px 8px rgba(0,0,0,0.1)',
          p: 2,
          zIndex: 1000,
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <Box
          display='flex'
          justifyContent='space-between'
          alignItems='center'
          flexWrap='wrap'
          sx={{
            width: '100%',
            maxWidth: '950px',
            margin: '0 auto',
            px: 2,
          }}
        >
          <Box>
            <Typography variant='body1' fontWeight='bold'>
              Summary
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              Your Plan
            </Typography>
          </Box>
          <Box display='flex' alignItems='center' gap={1} flexWrap='wrap'>
            <Typography variant='body2'>Monthly</Typography>
            <Tooltip
              title={
                isYearlyPlan(fetchedCurrentPlan?.subscription?.leads?.planId) ||
                  isYearlyPlan(fetchedCurrentPlan?.subscription?.sendingWarmup?.planId)
                  ? 'You cannot downgrade from a yearly plan to monthly'
                  : ''
              }
            >
              <span>
                <FormControlLabel
                  control={
                    <IOSSwitch
                      checked={isSwitchOn}
                      onChange={handleSwitchChange}
                      disabled={
                        isYearlyPlan(fetchedCurrentPlan?.subscription?.leads?.planId) ||
                        isYearlyPlan(fetchedCurrentPlan?.subscription?.sendingWarmup?.planId)
                      }
                    />
                  }
                  sx={{ marginRight: 0 }}
                />
              </span>
            </Tooltip>
            <Typography variant='body2'>Yearly</Typography>
            <Divider orientation='vertical' flexItem sx={{ borderColor: 'grey.400' }} />
            <Box>
              <Typography variant='body2' color='text.secondary'>
                Billed {isSwitchOn ? 'yearly' : 'monthly'}
              </Typography>
              <Typography variant='body2' fontWeight='bold'>
                ${total}/{isSwitchOn ? 'yr' : 'mo'}
              </Typography>
            </Box>
          </Box>
          <Box ml={{ xs: 0, md: 1 }} mt={{ xs: 2, md: 0 }}>
            <Button variant='contained' size='medium' onClick={handleConfirmPlanUpdate} disabled={total === 0}>
              Checkout: ${total}
            </Button>
          </Box>
        </Box>
      </Box>
    </>
  )
}

export default CustomizePlan